import { formatArtists } from '@akpl/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { api } from '../../api';
import searchIcon from '../assets/search.svg';
import { useDebounced } from '../catalog';
import { useLocale, useSongAccess, useWebT } from '../hooks';
import { useWebUi, type WebSong } from '../store';
import { Art, Badge } from './primitives';

const SUGGESTIONS = 3;
const DEBOUNCE_MS = 250;
const MAX_QUERY = 100;

function Suggestion({ song, onPick }: { song: WebSong; onPick: () => void }) {
  const locale = useLocale();
  const openOverlay = useWebUi((s) => s.openOverlay);
  const { badge } = useSongAccess(song);
  return (
    <button
      type="button"
      data-testid="suggestion"
      onClick={() => {
        onPick();
        openOverlay({ kind: 'song', song });
      }}
      className="flex h-14 w-full items-center gap-3 rounded-[10px] px-2.5 py-2 text-left hover:bg-surface-3 focus-visible:bg-surface-3"
    >
      <Art seed={song.id} imageUrl={song.imageUrl} width={40} radius={6} className="size-10" />
      <span className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="truncate text-[14px] font-semibold text-ink">{song.title}</span>
        <span className="truncate text-[12px] text-muted">
          {formatArtists(song.artists, locale)}
        </span>
      </span>
      {badge ? <Badge type={badge} className="shrink-0" /> : null}
    </button>
  );
}

/** E-28: three songs and "see all", under the search box of the nav. */
export function SearchSuggestions({ query, onClose }: { query: string; onClose: () => void }) {
  const t = useWebT();
  const q = useDebounced(query.trim().slice(0, MAX_QUERY), DEBOUNCE_MS);
  const results = useQuery({
    queryKey: ['web', 'search', 'suggestions', q],
    queryFn: ({ signal }) => api.search({ query: { q }, signal }),
    enabled: q.length > 0,
    staleTime: 60_000,
  });
  if (!q) return null;
  const songs = results.data?.songs.slice(0, SUGGESTIONS) ?? [];
  return (
    <div
      role="group"
      aria-label={t('web:home.search.suggestions')}
      data-testid="search-suggestions"
      // Keeps the focus in the input while a row is pressed: the blur would close the panel first.
      onMouseDown={(event) => event.preventDefault()}
      className="absolute top-[58px] left-[-85px] z-40 flex w-[560px] max-w-[calc(100vw-48px)] flex-col gap-0.5 overflow-hidden rounded-[14px] bg-surface-2 p-2"
    >
      {songs.map((song) => (
        <Suggestion key={song.id} song={song} onPick={onClose} />
      ))}
      <Link
        to="/buscar"
        search={{ q }}
        onClick={onClose}
        className="flex items-center gap-2 rounded-[10px] p-2.5 text-[14px] font-semibold text-cyan hover:bg-surface-3"
      >
        <img src={searchIcon} alt="" width={16} height={16} />
        <span className="truncate">{t('web:home.search.seeAllResults', { q })}</span>
      </Link>
    </div>
  );
}
